type StyleValue = string | number;
type StyleVars = Record<string, StyleValue>;
type StyleProps = Record<string, StyleValue>;

export class StyleTxn {
  public pendingVars = new Map<Element, StyleVars>();
  public pendingProps = new Map<Element, StyleProps>();
  public isOpen = false;

  private canUseTypedOM(el: Element): boolean {
    return (
      "attributeStyleMap" in el &&
      typeof CSS !== "undefined" &&
      typeof CSS.number === "function" &&
      typeof CSS.px === "function"
    );
  }

  private writeVar(el: Element, key: string, value: StyleValue): void {
    const style = (el as HTMLElement).style;

    if (this.canUseTypedOM(el) && typeof value === "number" && Number.isFinite(value)) {
      try {
        const styleMap = (el as any).attributeStyleMap;
        styleMap.set(key, CSS.number(value));
        return;
      } catch {
        // Fall back to string-based writes for unsupported value/syntax pairs.
      }
    }

    style.setProperty(key, String(value));
  }

  public begin(): void {
    if (this.isOpen) return;
    this.isOpen = true;
  }

  public setVars(el: Element, vars: StyleVars): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set custom properties.");
      return;
    }
    const currentVars = this.pendingVars.get(el) ?? {};
    for (const [k, v] of Object.entries(vars)) {
      if (currentVars[k] === v) continue;
      currentVars[k] = v;
    }
    this.pendingVars.set(el, currentVars);
  }

  public setVar(el: Element, key: string, value: StyleValue): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set custom properties.");
      return;
    }
    const currentVars = this.pendingVars.get(el) ?? {};
    if (currentVars[key] === value) return;
    currentVars[key] = value;
    this.pendingVars.set(el, currentVars);
  }

  /**
   * Directly sets a CSS custom property (variable) on an element using CSS Typed OM
   * for improved performance. Falls back to string-based assignment if not supported.
   * Note: This writes immediately. Use `begin()`, `setVar()`, `commit()` for batched writes.
   */
  public setVarDirect(el: Element, key: string, value: StyleValue): void {
    this.writeVar(el, key, value);
  }

  public setProps(el: Element, props: StyleProps): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set standard properties.");
      return;
    }
    const currentProps = this.pendingProps.get(el) ?? {};
    for (const [k, v] of Object.entries(props)) {
      if (currentProps[k] === v) continue;
      currentProps[k] = v;
    }

    this.pendingProps.set(el, currentProps);
  }

  public setProp(el: Element, key: string, value: StyleValue): void {
    if (!this.isOpen) {
      console.warn("StyleTxn: call begin() first to set standard properties.");
      return;
    }
    const currentProps = this.pendingProps.get(el) ?? {};
    if (currentProps[key] === value) return;
    currentProps[key] = value;
    this.pendingProps.set(el, currentProps);
  }

  public run(fn: () => void): void {
    const alreadyOpen = this.isOpen;
    if (!alreadyOpen) {
      this.begin();
    }
    try {
      fn();
      if (!alreadyOpen) {
        this.commit();
      }
    } catch (error) {
      if (!alreadyOpen) {
        this.cancel();
      }
      throw error;
    }
  }

  public commit(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    for (const [el, vars] of this.pendingVars) {
      for (const [k, v] of Object.entries(vars)) {
        this.writeVar(el, k, v);
      }
    }
    this.pendingVars.clear();

    for (const [el, props] of this.pendingProps) {
      const style = (el as HTMLElement).style;
      for (const [k, v] of Object.entries(props)) {
        (style as any)[k] = String(v);
      }
    }
    this.pendingProps.clear();
  }

  public cancel(): void {
    this.pendingVars.clear();
    this.pendingProps.clear();
    this.isOpen = false;
  }
}

export const styleTxn = new StyleTxn();
